<template>
    <div>

        <!-- Initial Buttons -->
        <div v-if="!isLoggedIn">
            <Button class="new-btn reserve" variant="sec-pine" id="loginBeforeReserve-btn" :disabled="disableReserveButton" @click="loginBeforeReserve">
                <b-spinner class="spinner" variant="light" label="Spinning" v-if="loading" />
                {{$t('pay.reserve')}}
            </Button>
        </div>
        <div v-else>
            <Button class="new-btn reserve" variant="sec-pine" id="reserve-btn" :disabled="disableReserveButton" @click="initPayment">
                <b-spinner class="spinner" variant="light" label="Spinning" v-if="loading" />
                {{$t('pay.reserve')}}
            </Button>
        </div>

        <!-- Modal Warning -->
        <b-modal id="modal-warning" ref="modalWarnings" modal-class="fx-modal" centered hide-header hide-footer size="md">
            <div class="fx-al">
                <button type="button" class="fx-x" :aria-label="$t('ficha_nueva.close')" @click="$refs.modalWarnings.hide()">×</button>
                <div class="fx-al-ico" aria-hidden="true">!</div>
                <p class="fx-al-t">{{ $t('pay.warning') }}</p>
                <ul class="fx-al-list">
                    <li v-for="(m, i) in listaErrores" :key="i">{{ m }}</li>
                </ul>
                <div class="fx-al-acts">
                    <button v-if="profileComplete" type="button" class="fx-btn fx-btn--primary" @click="completeData">{{ $t('dashboard-side-menu.finish-registration') }}</button>
                    <button v-else type="button" class="fx-btn fx-btn--primary" @click="$refs.modalWarnings.hide()">{{ $t('ficha_nueva.understood') }}</button>
                </div>
            </div>
        </b-modal>

        <!-- Modal Success -->
        <b-modal id="modal-success" ref="modalSuccess" modal-class="fx-modal" centered hide-header hide-footer size="md" @hidden="redirectDashboard">
            <div class="fx-al">
                <div class="fx-al-ico fx-al-ico--ok" aria-hidden="true">✓</div>
                <p class="fx-al-t">{{ $t('pay.congratulations') }}</p>
                <p class="fx-al-txt">{{ $t('pay.success-message') }}</p>
                <div class="fx-al-acts">
                    <button type="button" class="fx-btn fx-btn--primary" @click="$refs.modalSuccess.hide()">{{ $t('ficha_nueva.pay.see_bookings') }}</button>
                </div>
            </div>
        </b-modal>

        <!-- Payment Modal: ancho y del mismo tamaño en los 3 pasos -->
        <b-modal
            id="modal-pago"
            ref="modalPago"
            modal-class="fx-modal fx-modal--pago"
            centered
            size="xl"
            hide-header
            hide-footer
            @show="resetStep"
            @hidden="cancelReservation"
        >
            <div class="fx-pg">
                <button type="button" class="fx-x" :aria-label="$t('ficha_nueva.close')" @click="$refs.modalPago.hide()">×</button>

                <div class="fx-pg-head">
                    <h2 class="fx-pg-title">{{ pasos[stepIndex] }}</h2>
                    <ol class="fx-wz" :aria-label="$t('ficha_nueva.pay.steps')">
                        <li v-for="(t, i) in pasos" :key="i" :class="{ 'is-done': i < stepIndex, 'is-now': i === stepIndex }" :aria-current="i === stepIndex ? 'step' : null">
                            <span>{{ i < stepIndex ? '✓' : i + 1 }}</span>{{ t }}
                        </li>
                    </ol>
                </div>

                <div ref="pgScroll" class="fx-pg-scroll">

                    <!-- PASO 1 · Reserva -->
                    <div v-if="stepIndex === 0" class="fx-pg-cols">
                        <div class="fx-pg-col">
                            <div class="fx-pg-res">
                                <img v-if="fotoReserva" :src="fotoReserva" alt="" />
                                <span v-else class="fx-pg-res-ph"></span>
                                <div>
                                    <p class="fx-pg-res-t">{{ tituloReserva }}</p>
                                    <p class="fx-pg-res-d">ID {{ accommodation && accommodation.idalojamiento }}</p>
                                    <p v-if="checkInLabel" class="fx-pg-res-f">{{ checkInLabel }} → {{ checkOutLabel }} · {{ $tc('ficha_nueva.nights', stayDays, { n: stayDays }) }}</p>
                                </div>
                            </div>

                            <!-- Modelo de hoy (4 %) -->
                            <template v-if="hfCommissionEnabled">
                                <div class="fx-pg-box fx-pg-box--hf">
                                    <p class="fx-pg-box-t"><span class="fx-pg-n">1</span>{{ $t('simulator.rs-pay-today') }}</p>
                                    <p class="fx-pg-row"><span>{{ stayDays >= 30 ? $t('simulator.rs-first-month') : $t('simulator.rs-stay') }}<small v-if="tramoHoy" class="fx-pg-row-d">{{ tramoHoy }}</small></span><span>{{ cargoPlataforma | convertAndFormatPrice }}</span></p>
                                    <p class="fx-pg-row"><span>{{ $t('ficha_nueva.sim.service') }}</span><span>{{ rentalCommission | convertAndFormatPrice }}</span></p>
                                    <p v-if="discount.amount > 0" class="fx-pg-row fx-pg-row--ok"><span>{{ $t('simulator.coupon') }}</span><span>- {{ discount | convertAndFormatPrice }}</span></p>
                                    <p class="fx-pg-row fx-pg-row--total"><span>{{ $t('simulator.rs-total-today') }}</span><span>{{ finalPrice | convertAndFormatPrice }}</span></p>
                                    <p class="fx-pg-fine">{{ $t('simulator.rs-gateway-not-included') }}</p>
                                </div>

                                <div v-if="remainingToHost.amount > 0 && filasDespues.length" class="fx-pg-box">
                                    <p class="fx-pg-box-t"><span class="fx-pg-n fx-pg-n--muted">2</span>{{ $t('ficha_nueva.sim.after_host') }}</p>
                                    <p v-for="f in filasDespues" :key="f.k" class="fx-pg-row">
                                        <span>{{ f.t }}<small class="fx-pg-row-d">{{ f.d }}</small></span>
                                        <span>{{ f.monto | convertAndFormatPrice }}</span>
                                    </p>
                                    <p class="fx-pg-row fx-pg-row--total"><span>{{ $t('ficha_nueva.sim.after_total') }}</span><span>{{ remainingToHost | convertAndFormatPrice }}</span></p>
                                    <p v-if="planPago.completos >= 2" class="fx-pg-fine">{{ $t('simulator.rs-monthly-detail', { day: checkInDay }) }}</p>
                                    <p class="fx-pg-fine">{{ $t('ficha_nueva.sim.how') }}</p>
                                </div>
                            </template>

                            <!-- Modelo anterior (garantía / estadía + cargo por servicio) -->
                            <div v-else class="fx-pg-box">
                                <p class="fx-pg-box-t">{{ $t('simulator.payment-by-reservation') }}</p>
                                <p class="fx-pg-row"><span>{{ $t('simulator.stay-total') }}</span><span>{{ stayTotal | convertAndFormatPrice }}</span></p>
                                <p class="fx-pg-row"><span>{{ etiquetaCargoPlataforma }}</span><span>{{ cargoPlataforma | convertAndFormatPrice }}</span></p>
                                <p class="fx-pg-row"><span>{{ etiquetaComision }}</span><span>{{ rentalCommission | convertAndFormatPrice }}</span></p>
                                <p v-if="discount.amount > 0" class="fx-pg-row fx-pg-row--ok"><span>{{ $t('simulator.coupon') }}</span><span>- {{ discount | convertAndFormatPrice }}</span></p>
                                <p class="fx-pg-row fx-pg-row--total"><span>{{ $t('simulator.total') }}*</span><span>{{ finalPrice | convertAndFormatPrice }}</span></p>
                                <p class="fx-pg-fine">* {{ $t('simulator.gateway-charge-not-included') }}</p>
                            </div>
                        </div>

                        <div class="fx-pg-col">
                            <template v-if="hfCommissionEnabled">
                                <div v-if="remainingToHost.amount > 0" class="fx-pg-box">
                                    <p class="fx-pg-box-t">{{ $t('ficha_nueva.pay.schedule') }}</p>
                                    <table class="fx-pg-cal">
                                        <thead><tr><th scope="col">{{ $t('simulator.rs-schedule-when') }}</th><th scope="col">{{ $t('simulator.rs-schedule-what') }}</th><th scope="col">{{ $t('simulator.rs-schedule-who') }}</th></tr></thead>
                                        <tbody>
                                            <tr v-for="(f, i) in calendarioPagos" :key="i">
                                                <td>{{ f.cuando }}</td>
                                                <td>{{ f.que }}<small v-if="f.monto">{{ f.monto | convertAndFormatPrice }}</small></td>
                                                <td><span :class="['fx-who', { 'fx-who--hf': f.hf }]">{{ f.hf ? 'House & Flats' : $t('simulator.rs-schedule-host') }}</span></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <p class="fx-pg-total">
                                    <span>{{ $t('simulator.rs-stay-total') }} <small>{{ $t('simulator.rs-without-warranty') }}</small></span>
                                    <strong>{{ totalEstadia | convertAndFormatPrice }}</strong>
                                </p>

                                <div class="fx-pg-war">
                                    <p class="fx-pg-box-t">{{ $t('simulator.rs-warranty') }} <small>{{ $t('simulator.rs-apart-from-total') }}</small></p>
                                    <div class="fx-pg-war-g">
                                        <div><span>{{ $t('simulator.rs-warranty') }}</span><strong>{{ $t('simulator.rs-warranty-months') }}</strong></div>
                                        <div><span>{{ $t('simulator.rs-amount') }}</span><strong>{{ warrantyExample | convertAndFormatPrice }}</strong></div>
                                        <div><span>{{ $t('simulator.rs-payment-date') }}</span><strong>{{ checkInLabel }}</strong></div>
                                    </div>
                                    <p class="fx-pg-war-n">{{ $t('simulator.rs-warranty-note') }}</p>
                                </div>
                            </template>

                            <div v-else class="fx-pg-box">
                                <p class="fx-pg-box-t">{{ $t('simulator.payment-during-stay') }}</p>
                                <p class="fx-pg-legacy">{{ mensajeDePago }}</p>
                                <p class="fx-pg-fine">{{ $t('simulator.host-rules-note') }}</p>
                            </div>

                            <div class="fx-pg-cup">
                                <Coupon @coupon="setCoupon" :reservation="reservation" />
                            </div>
                        </div>
                    </div>

                    <!-- PASO 2 · Detalle del pago -->
                    <div v-else-if="stepIndex === 1" class="fx-pg-narrow">
                        <div class="fx-pg-box">
                            <p class="fx-pg-row"><span>{{ $t('simulator.stay-total') }}</span><span>{{ stayTotal | convertAndFormatPrice }}</span></p>
                            <p class="fx-pg-row"><span>{{ etiquetaCargoPlataforma }}</span><span>{{ cargoPlataforma | convertAndFormatPrice }}</span></p>
                            <p class="fx-pg-row"><span>{{ etiquetaComision }}</span><span>{{ rentalCommission | convertAndFormatPrice }}</span></p>
                            <p v-if="discount.amount > 0" class="fx-pg-row fx-pg-row--ok"><span>{{ $t('simulator.coupon') }}</span><span>- {{ discount | convertAndFormatPrice }}</span></p>
                            <p class="fx-pg-row fx-pg-row--total"><span>{{ $t('pay.total') }}*</span><span>{{ finalPrice | convertAndFormatPrice }}</span></p>
                            <p class="fx-pg-fine">* {{ $t('simulator.gateway-charge-not-included') }}</p>
                        </div>
                    </div>

                    <!-- PASO 3 · Atención -->
                    <div v-else class="fx-pg-narrow">
                        <div class="fx-pg-att">
                            <span class="fx-pg-att-ico" aria-hidden="true">i</span>
                            <p>{{ $t('pay.payment-message') }}</p>
                        </div>
                    </div>
                </div>

                <div class="fx-pg-foot">
                    <label v-if="stepIndex === 0" class="fx-pg-terms">
                        <input v-model="terms" type="checkbox" />
                        <span>{{ $t('ficha_nueva.pay.accept') }} <a target="_blank" rel="noopener" href="/tyc">{{ $t('ficha_nueva.pay.terms') }}</a></span>
                    </label>
                    <span v-else></span>
                    <div class="fx-pg-acts">
                        <button v-if="stepIndex === 0" type="button" class="fx-btn fx-btn--ghost" @click="$refs.modalPago.hide()">{{ $t('ficha_nueva.cancel') }}</button>
                        <button v-else type="button" id="reserve-back-button" class="fx-btn fx-btn--ghost" :disabled="!terms" @click="irPaso(stepIndex - 1)">{{ $t('pay.back') }}</button>
                        <button v-if="stepIndex !== 2" type="button" id="reserve-continue-button" class="fx-btn fx-btn--primary" :disabled="!terms" @click="irPaso(stepIndex + 1)">{{ $t('pay.continue') }}</button>
                        <button v-else type="button" id="reserve-button" class="fx-btn fx-btn--primary" :disabled="!terms || reserving" @click="reservar">
                            <span v-if="loading" class="fx-spin" aria-hidden="true"></span>
                            {{ $t('pay.reserve') }}
                        </button>
                    </div>
                    <p v-if="stepIndex === 0 && !terms" class="fx-pg-need" v-html="$t('pay.accept-terms')"></p>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapState, mapGetters } from "vuex";
import HotelDatePicker from "vue-hotel-datepicker";
import CarouselList from "@/components/Carousel/CarouselList";
import Coupon from '../Coupon/Coupon';
import { planDePagos } from "@/Utils/planDePagos";
import { saveRedirectUrl } from "@/Utils/Utils";
import Button from "@/components/Ui/Button.vue";
import {trackEvent} from "@/Utils/tracking";

export default {
    name: "Pay",
    props: ["image", "moneda", "guest"],
    data() {
        return {
            stepIndex: 0,
            url: process.env.VUE_APP_S3_URL,
            api: process.env.VUE_APP_API_URL,
            loading: false,
            reserving: false,
            errors: {},
            success: false,
            terms: false,
            initReserva: false,
            huespedes: null,
            profileComplete: false,
            coupon: {
                percentage: 0,
                discount: 0
            }
        };
    },
    components: {
        Button,
        HotelDatePicker,
        CarouselList,
        Coupon
    },

    mounted() {
        // Si venimos de verificar identidad en Didit, retomar el flujo de reserva.
        this.tryResumeAfterKyc();
    },

    methods: {

        // Mismo formato que el filtro `convertAndFormatPrice`, para poder
        // interpolar montos dentro de los textos traducidos.
        formatPrice(amount) {
            return Vue.filter('convertAndFormatPrice')(amount);
        },

        // 'YYYY-MM-DD' → "16 oct" en el idioma de la página (sin correr el día).
        fechaCorta(iso) {
            if (!iso) return '';
            const [y, m, d] = String(iso).split('-').map(Number);
            return new Date(y, m - 1, d, 12).toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short' }).replace('.', '');
        },

        ...mapActions("simulator", [
            'getAvailable',
        ]),
        
        ...mapActions([
            "createReservation",
            "makeReservationRequest",
            "checkUserData",
            "createDiditSession",
            "fetchSpacestCheckoutLink"
        ]),

        completeData() {
            saveRedirectUrl(this.$route);
            this.$router.push({ name: 'editar-perfil' });
        },

        loginBeforeReserve () {
            
            // Save the url the user was seeing
            saveRedirectUrl(this.$route);
            
            this.$toasted.show(this.$t('login.need-login'), {
                theme: "toasted-primary",
                position: "bottom-right",
                duration: 2500,
                className: "toasted"
            });
            
            setTimeout(() => {
                this.$router.push({ name: "login" });
            }, 2500);
            
        },

        priceCurrency() {

            let currency = localStorage.getItem("currency_selected");

            switch (currency) {
                case "clp":
                    currency = "$CLP";
                    break;
                case "es_AR":
                    currency = "$ARS";
                    break;
                case "en_US":
                    currency = "$USD";
                    break;
                case "en_GB":
                    currency = "$GBP";
                    break;
                case "it_IT":
                    currency = "$EUR";
                    break;
                default:
                    currency = "$CLP";
            }
            return `${currency}`;
        },
        // Cambio de paso del modal (antes lo hacía form-wizard)
        irPaso(n) {
            this.stepIndex = Math.max(0, Math.min(2, n));
            this.$nextTick(() => { if (this.$refs.pgScroll) this.$refs.pgScroll.scrollTop = 0; });
        },

        setCoupon(payload){
            this.coupon = {...this.coupon, ...payload };
        },
        
        resetStep() {
            this.stepIndex = 0;
            trackEvent('NewEvent', 'begin_checkout', {
                currency: this.accommodation.moneda,
                value: Number(this.simulation.total),
                items: {
                    item_id: this.accommodationId
                }
            });
        },

        /**
         * Comprueba si el usuario tiene la identidad verificada.
         *
         * Resuelve true  → seguir con el flujo de reserva normal.
         * Resuelve false → se está redirigiendo a Didit, cortar acá.
         */
        ensureIdentityVerified() {
            return this.createDiditSession({})
                .then((res) => {
                    if (!res || !res.kyc_required) {
                        return true;
                    }

                    if (!res.url) {
                        // Sin URL no hay adónde mandarlo (por ejemplo, quedó en
                        // revisión manual). Lo dejamos pasar y que el backend
                        // corte al confirmar, con su mensaje.
                        return true;
                    }

                    this.redirectToKyc(res.url);
                    return false;
                })
                .catch(() => {
                    // Didit no respondió. Dejamos seguir: el backend igual bloquea
                    // la confirmación, así que no se abre ningún agujero.
                    return true;
                });
        },

        /**
         * Guarda el contexto de la reserva y sale a Didit.
         *
         * El store de Vuex no persiste entre recargas, así que las fechas se
         * guardan en sessionStorage para poder restaurarlas al volver.
         */
        redirectToKyc(url) {
            try {
                sessionStorage.setItem("hyf_kyc_pending", JSON.stringify({
                    mode: "pre-reservation",
                    returnPath: this.$route.fullPath,
                    checkInDate: this.checkInDate,
                    checkOutDate: this.checkOutDate,
                }));
            } catch (e) {
                // Sin sessionStorage el usuario vuelve a la ficha y elige fechas de nuevo.
            }

            window.location.href = url;
        },

        /**
         * Al volver de Didit verificado, la página de retorno deja una bandera y
         * restaura las fechas. Acá se reabre el flujo de reserva solo.
         */
        tryResumeAfterKyc() {
            let flagged = false;

            try {
                flagged = sessionStorage.getItem("hyf_kyc_resume") === "1";
            } catch (e) {
                return;
            }

            if (!flagged || !this.checkInDate || !this.checkOutDate) {
                return;
            }

            // Consumir la bandera antes de arrancar, para no reentrar.
            try {
                sessionStorage.removeItem("hyf_kyc_resume");
            } catch (e) {
                // nada que limpiar
            }

            this.initPayment();
        },

        /**
         * Handoff al checkout de Spacest.
         *
         * La URL no viene en la ficha: se pide acá, y el backend solo la entrega
         * si la identidad ya está verificada. Si contesta 403 es porque el KYC
         * se cayó entre medio (sesión vencida, rechazo tardío del webhook): en
         * ese caso volvemos a mandarlo a Didit y al regresar retoma solo, porque
         * tryResumeAfterKyc vuelve a entrar por initPayment.
         */
        goToSpacestCheckout() {
            return this.fetchSpacestCheckoutLink({
                idalojamiento: this.$route.params.idalojamiento,
                fecha_inicio: this.checkInDate,
                fecha_termino: this.checkOutDate,
            })
                .then((res) => {
                    if (!res || !res.url) {
                        throw new Error("sin url de checkout");
                    }

                    // No se toca la URL: los UTM son la atribución de Spacest.
                    window.location.href = res.url;
                })
                .catch((error) => {
                    const data = error.response ? error.response.data : null;

                    if (error.response && error.response.status === 403 && data && data.kyc_required) {
                        if (data.verification_url) {
                            this.redirectToKyc(data.verification_url);
                            return;
                        }

                        this.loading = false;
                        this.errors = { kyc: [data.message] };
                        this.$refs["modalWarnings"].show();
                        return;
                    }

                    this.loading = false;
                    this.errors = {
                        spacest: [
                            (data && data.message)
                                ? data.message
                                : "No pudimos abrir la reserva de este alojamiento. Intentá de nuevo en unos minutos."
                        ]
                    };
                    this.$refs["modalWarnings"].show();
                });
        },

        initPayment() {

            this.errors = {};

            this.loading = true;

            let data = {
                idalojamiento: this.$route.params.idalojamiento,
                fecha_inicio: this.checkInDate,
                fecha_termino: this.checkOutDate,
                language: this.$i18n.locale,
            };

            if (data.fecha_inicio == null) {
                this.errors.fecha_inicio =
                    this.$i18n.locale === "es"
                        ? ["Por favor, introduzca la fecha de inicio"]
                        : ["Please enter the start date"];
            }

            if (data.fecha_termino == null) {
                this.errors.fecha_termino =
                    this.$i18n.locale === "es"
                        ? ["Por favor, introduzca la fecha de término"]
                        : ["Please enter the end date"];
            }
            
            this.profileComplete = false;

            this.checkUserData().then((response) => {
                if(response == false) {
                    this.errors = {};
                    this.errors.register = this.$i18n.locale === "es" ? ["Para hacer una reserva, ahora primero debe completar su registro."] : ["To make a reservation, you must now complete your registration."];
                    this.$refs["modalWarnings"].show();
                    this.loading = false;
                    this.profileComplete = true;
                }else{
                    if (this.isEmpty(this.errors)) {
                        // Verificación de identidad ANTES de arrancar la reserva.
                        // Si el usuario no está verificado, esto redirige a Didit
                        // y no se crea nada todavía.
                        this.ensureIdentityVerified()
                            .then((verified) => {
                                if (!verified) {
                                    // Redirigiendo a Didit: no seguimos.
                                    return null;
                                }

                                // Inventario de Spacest: la reserva se hace en el
                                // sitio de ellos. Ya verificamos al huésped, así
                                // que ahora lo soltamos al checkout y acá no se
                                // crea ninguna reserva ni se abre el modal de pago.
                                if (this.isSpacestAccommodation) {
                                    return this.goToSpacestCheckout();
                                }

                                return this.createReservation(data)
                                    .then(() => {
                                        this.loading = false;
                                        this.$refs["modalPago"].show();
                                    });
                            })
                            .catch(error => {
                                this.success = false;
                                this.errors = error.response ? error.response.data : {};
                                this.loading = false;
                                this.$refs["modalWarnings"].show();
                            });
                    } else {
                        this.loading = false;
                        this.$refs["modalWarnings"].show();
                    }
                }
            })
            
        },

        reservar() {
            this.loading = true;
            this.initReserva = true;
            this.reserving = true;
            this.makeReservationRequest({idreserva: this.reservation.idreserva, coupon: this.coupon })
                .then(response => {
                    this.loading = false;
                    this.$refs["modalSuccess"].show();
                    this.$refs["modalPago"].hide();
                    this.$store.commit("setReservation", {});
                })
                .catch(error => {
                    this.loading = false;
                    this.reserving = false;

                    const data = error.response ? error.response.data : null;

                    // El backend bloqueó la reserva por falta de verificación de identidad.
                    // La reserva sigue viva en estado 'created': no se perdió nada.
                    if (error.response && error.response.status === 403 && data && data.kyc_required) {
                        this.handleKycRequired(data);
                        return;
                    }

                    this.initReserva = false;
                    this.errors = data || {};
                    this.$refs["modalWarnings"].show();
                });
        },

        /**
         * Guarda el contexto de la reserva y manda al huésped a verificar su
         * identidad en Didit. Al volver, VerificacionIdentidadRetorno retoma
         * la confirmación con estos mismos datos.
         */
        handleKycRequired(data) {
            // 'in_review' significa que ya se verificó y Didit lo está revisando
            // a mano: no hay adónde mandarlo, solo esperar.
            if (data.kyc_status === "in_review" || !data.verification_url) {
                this.initReserva = false;
                this.errors = { kyc: [data.message] };
                this.$refs["modalWarnings"].show();
                return;
            }

            try {
                sessionStorage.setItem("hyf_kyc_pending", JSON.stringify({
                    idreserva: this.reservation.idreserva,
                    coupon: this.coupon,
                    returnPath: this.$route.fullPath,
                }));
            } catch (e) {
                // Modo privado sin sessionStorage: la reserva se retoma a mano
                // desde "Reservas realizadas".
            }

            window.location.href = data.verification_url;
        },

        isEmpty(obj) {
            for (var prop in obj) {
                if (obj.hasOwnProperty(prop)) return false;
            }
            return true;
        },
        cancelReservation() {
            if (!this.initReserva) {
                this.$emit("canceled");
            }
            this.coupon.percentage = 0;
            this.coupon.discount = 0;
        },
        redirectDashboard() {
            this.$router.history.push({ name: "reservations-made"});
        },

    },
    computed: {
        
        ...mapState("simulator", [
            'accommodationId',
            'initDate',
            'endDate',
            'available',
            'simulation'
        ]),
        
        ...mapState('search', [
            'checkInDate',
            'checkOutDate',
        ]),

        // TODO: add the currency to the simulation response and remove this
        ...mapState("accommodation", [
            'accommodation',
        ]),
        
        ...mapState(["reservation", "accomodation", "totalDays"]),
        ...mapGetters(["isLoggedIn"]),

        // Estadía completa: la base sobre la que se calcula la comisión HF.
        stayTotal() {
            const amount = Number(this.reservation.costo) || Number(this.simulation.stayTotal) || 0;
            return { 'amount': amount, 'currency': this.reservation.moneda }
        },

        // Comisión de House & Flats: se le descuenta al anfitrión del primer
        // mes. No se le suma al huésped; acá solo se usa para los textos.
        rentalCommission() {
            const amount = Number(this.reservation.comision) || Number(this.simulation.commission) || 0;
            return { 'amount': amount, 'currency': this.reservation.moneda }
        },

        commissionRate() {
            return Number(this.simulation.commissionRate) || 4;
        },

        // Primer mes de arriendo: lo único que se paga por la plataforma.
        firstMonth() {
            const amount = Number(this.simulation.firstMonth) || Number(this.simulation.total) || this.stayTotal.amount;
            return { 'amount': amount, 'currency': this.reservation.moneda }
        },

        /**
         * ¿El alojamiento se cobra con el modelo de hoy o con el anterior?
         * Lo define el backend según el modelo asignado al dueño. Si el dato no
         * llegó, se asume el modelo de hoy.
         */
        hfCommissionEnabled() {
            return this.simulation.hfCommissionEnabled !== false;
        },

        // Garantía de un mes: solo existe con el modelo anterior.
        warranty() {
            return { 'amount': Number(this.simulation.warranty) || 0, 'currency': this.reservation.moneda }
        },

        /**
         * Lo que cobra la plataforma además del cargo por servicio: el primer
         * mes con el modelo de hoy; con el anterior, la garantía, o la estadía
         * completa cuando es de menos de 30 días y no hay garantía.
         */
        cargoPlataforma() {
            if (this.hfCommissionEnabled) {
                return this.firstMonth;
            }

            if (this.warranty.amount > 0) {
                return this.warranty;
            }

            return { 'amount': Number(this.simulation.stay) || 0, 'currency': this.reservation.moneda }
        },

        etiquetaCargoPlataforma() {
            if (!this.hfCommissionEnabled) {
                return this.warranty.amount > 0
                    ? this.$t('payment.warranty-deposit')
                    : this.$t('simulator.stay');
            }

            return this.totalDays >= 30
                ? this.$t('simulator.first-month')
                : this.$t('simulator.stay');
        },

        /**
         * La etiqueta de la fila del cargo por servicio. Con el modelo anterior
         * es el "cargo por servicio" de siempre, sin la aclaración del medio mes.
         */
        etiquetaComision() {
            return this.hfCommissionEnabled
                ? this.$t('simulator.service-commission-half-month')
                : this.$t('simulator.service-charge');
        },

        /**
         * El párrafo que explica qué se está pagando.
         *
         * Con el modelo anterior vuelve el texto de siempre —los mensajes
         * `…-legacy`, que desglosan garantía (o estadía) y cargo por servicio—
         * en vez del que habla del primer mes y la comisión.
         */
        mensajeDePago() {
            const precio = this.formatPrice(this.finalPrice);

            if (!this.hfCommissionEnabled) {
                return this.totalDays >= 30
                    ? this.$t('simulator.payment-details-message-monthly-legacy', {
                        finalPrice: precio,
                        rentalWarranty: this.formatPrice(this.cargoPlataforma),
                        rentalCommission: this.formatPrice(this.rentalCommission),
                    })
                    : this.$t('simulator.payment-details-message-daily-legacy', {
                        finalPrice: precio,
                        rentalStay: this.formatPrice(this.cargoPlataforma),
                        rentalCommission: this.formatPrice(this.rentalCommission),
                    });
            }

            return this.totalDays >= 30
                ? this.$t('simulator.payment-during-stay-monthly', { finalPrice: precio })
                : this.$t('simulator.payment-during-stay-daily', { finalPrice: precio });
        },

        // Meses restantes: se los paga directamente al anfitrión.
        remainingToHost() {
            return { 'amount': Number(this.simulation.remainingToHost) || 0, 'currency': this.reservation.moneda }
        },

        // ── Resumen "Tu reserva" (modelo del 4%). Solo presentación: no cambia
        //    ningún monto, los reparte en líneas con los datos que ya manda
        //    el simulador.

        // Días de la estadía: los del simulador y, si faltan, los del store.
        stayDays() {
            return Number(this.simulation.totalDays) || Number(this.totalDays) || 0;
        },

        monthlyRent() {
            const amount = Number(this.simulation.monthlyRent) || this.firstMonth.amount;
            return { 'amount': amount, 'currency': this.reservation.moneda }
        },

        /**
         * Plan de pagos con fechas (ver Utils/planDePagos): primer mes hoy,
         * después cada mes y las noches que sobran, directo al anfitrión.
         */
        planPago() {
            const s = this.simulation;
            const llegada = String(this.reservation.fecha_inicio || this.checkInDate || this.initDate || '').substring(0, 10);
            return planDePagos({
                noches: this.stayDays, llegada, mensual: Number(s.monthlyRent) || this.firstMonth.amount,
                primerMes: this.cargoPlataforma.amount, restoAnfitrion: this.remainingToHost.amount,
            });
        },

        // "16 oct → 15 nov · 30 noches" del tramo que se paga hoy.
        tramoHoy() {
            const h = this.planPago.hoy;
            const fechas = h.desde && h.hasta ? `${this.fechaCorta(h.desde)} → ${this.fechaCorta(h.hasta)}` : '';
            return [fechas, h.noches ? this.$tc('ficha_nueva.nights', h.noches, { n: h.noches }) : ''].filter(Boolean).join(' · ');
        },

        /**
         * Filas de "Después, directo a tu anfitrión": cada mes con sus fechas y
         * las noches que sobran. Con 4 meses o más, los meses van en una fila.
         */
        filasDespues() {
            const cur = this.reservation.moneda;
            const rango = (a, b) => (a && b ? `${this.fechaCorta(a)} → ${this.fechaCorta(b)}` : '');
            const meses = this.planPago.despues.filter(t => t.tipo === 'mes');
            const noches = this.planPago.despues.find(t => t.tipo === 'noches');
            const out = [];
            if (meses.length >= 4) {
                const u = meses[meses.length - 1];
                out.push({
                    k: 'meses', t: this.$t('ficha_nueva.sim.months_range', { a: meses[0].n, b: u.n }),
                    d: this.$t('ficha_nueva.sim.each_month', { price: this.formatPrice(this.monthlyRent) }) + ' · ' + rango(meses[0].desde, u.hasta),
                    monto: { amount: meses.reduce((a, t) => a + t.monto, 0), currency: cur },
                });
            } else {
                meses.forEach(t => out.push({ k: 'mes' + t.n, t: this.$t('ficha_nueva.pay.month_n', { n: t.n }), d: rango(t.desde, t.hasta), monto: { amount: t.monto, currency: cur } }));
            }
            if (noches) {
                out.push({
                    k: 'noches', t: this.$tc('ficha_nueva.sim.extra_nights', noches.noches, { n: noches.noches }),
                    d: rango(noches.desde, noches.hasta) + ' · ' + this.$t('ficha_nueva.sim.per_night', { price: '≈ ' + this.formatPrice({ amount: this.planPago.porNoche, currency: cur }) }),
                    monto: { amount: noches.monto, currency: cur },
                });
            }
            return out;
        },

        // Total de la estadía: lo que se paga hoy más lo que va al anfitrión.
        totalEstadia() {
            return { 'amount': this.finalPrice.amount + this.remainingToHost.amount, 'currency': this.reservation.moneda }
        },

        // Garantía de ejemplo: siempre un mes (el primer mes de arriendo).
        warrantyExample() {
            return this.firstMonth;
        },

        // Fecha de entrada como [año, mes, día], sin pasar por Date para que
        // la zona horaria no la corra un día.
        checkInParts() {
            const raw = String(this.reservation.fecha_inicio || this.checkInDate || '').substring(0, 10);
            const parts = raw.split('-').map(Number);
            return parts.length === 3 && parts.every(n => n > 0) ? parts : null;
        },

        checkInDay() {
            return this.checkInParts ? this.checkInParts[2] : '';
        },

        checkInLabel() {
            if (!this.checkInParts) {
                return '';
            }
            const [y, m, d] = this.checkInParts;
            return new Date(y, m - 1, d)
                .toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short', year: 'numeric' })
                .replace('.', '');
        },

        // El cupón descuenta sobre la comisión por servicio.
        discount() {
            const discountAmount = this.rentalCommission.amount * this.coupon.percentage / 100;
            return { 'amount': discountAmount, 'currency': this.reservation.moneda };
        },

        // Lo que se abona ahora a House & Flats: el primer mes de arriendo más
        // la comisión por servicio, menos el descuento del cupón. Con el modelo
        // anterior, la garantía (o la estadía) más la comisión.
        finalPrice() {
            const total = this.cargoPlataforma.amount + this.rentalCommission.amount - this.discount.amount;
            return { 'amount': Math.max(0, total), 'currency': this.reservation.moneda };
        },
        
        pasos() {
            return [this.$t('pay.title'), this.$t('pay.payment-detail'), this.$t('pay.attention')];
        },

        // Avisos del modal de advertencia: los errores vienen como { campo: [mensajes] }
        // (o un texto suelto desde la API).
        listaErrores() {
            const out = [];
            Object.keys(this.errors || {}).forEach(k => {
                const v = this.errors[k];
                (Array.isArray(v) ? v : [v]).forEach(m => { if (m && typeof m !== 'object') out.push(String(m)); });
            });
            return out;
        },

        tituloReserva() {
            const a = this.accommodation;
            if (!a) return '';
            if (a.propiedad && a.propiedad.nombre) return a.propiedad.nombre + ' · #' + a.idalojamiento;
            return this.$t('accommodation.title.' + a.tipo + '-' + a.categoria);
        },

        fotoReserva() {
            const a = this.accommodation;
            const f = a && Array.isArray(a.photos) && a.photos.length ? a.photos[0].url : '';
            if (!f) return '';
            if (f.indexOf('http') === -1) return (process.env.VUE_APP_S3_URL || '') + f;
            return f;
        },

        checkOutLabel() {
            const raw = String(this.reservation.fecha_termino || this.checkOutDate || '').substring(0, 10);
            const [y, m, d] = raw.split('-').map(Number);
            if (!y || !m || !d) return '';
            return new Date(y, m - 1, d).toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '');
        },

        /**
         * Calendario de pagos con las fechas reales de la estadía: hoy a House &
         * Flats (primer mes + cargo); después, cada mes y las noches que sobran,
         * al anfitrión. Mismos tramos que filasDespues.
         */
        calendarioPagos() {
            if (!this.checkInParts) return [];
            const cur = this.reservation.moneda;
            const filas = [{ cuando: this.$t('simulator.rs-schedule-row1-when'), que: this.$t('ficha_nueva.sim.today_what'), monto: this.finalPrice, hf: true }];
            this.planPago.despues.forEach(t => {
                filas.push({
                    cuando: this.$t('ficha_nueva.pay.from_date', { date: this.fechaCorta(t.desde) }),
                    que: t.tipo === 'mes' ? this.$t('ficha_nueva.pay.month_n', { n: t.n }) : this.$tc('ficha_nueva.sim.extra_nights', t.noches, { n: t.noches }),
                    monto: { amount: t.monto, currency: cur }, hf: false,
                });
            });
            if (filas.length > 6) {
                const resto = filas.length - 4;
                return [...filas.slice(0, 3), { cuando: '…', que: this.$tc('ficha_nueva.pay.more_months', resto, { n: resto }), hf: false }, filas[filas.length - 1]];
            }
            return filas;
        },

        disableReserveButton() {
            // Quitar la lógica que bloquea el botón de reservar: dejar siempre habilitado
            return false;
        },

        /**
         * Alojamiento traído del feed de Spacest. Su reserva no se hace acá:
         * después de verificar identidad se redirige al checkout de ellos.
         *
         * El backend adjunta `spacest` solo a los alojamientos del feed, así que
         * su sola presencia alcanza para distinguirlos.
         */
        isSpacestAccommodation() {
            return !!(this.accommodation && this.accommodation.spacest);
        },
    },
    
    watch: {
        'accommodationId': function() {
            if (this.accommodationId && this.accommodationId > 0 && this.initDate && this.endDate) {
                this.getAvailable();
            }
        },
        'initDate': function(newVal) {
            if (newVal && this.endDate && newVal < this.endDate && this.accommodationId && this.accommodationId > 0) {
                this.getAvailable();
            }
        },
        'endDate': function(newVal) {
            if (newVal && this.initDate && newVal > this.initDate && this.accommodationId && this.accommodationId > 0) {
                this.getAvailable();
            }
        },

        // Las fechas restauradas al volver de Didit pueden llegar después de
        // montar el componente. Reintentamos cuando ya están las dos.
        'checkOutDate': function() {
            this.tryResumeAfterKyc();
        },
    }

};
</script>

<style lang="scss">

.wizard-nav-pills {
    li.active {
        .wizard-icon,
        .stepTitle
        {
            color: $grey-50 !important;
        }
    }
}

.wizard-tab-content {
    padding: 30px 0 10px 0 !important;
}

/* El diseño de .toasted (avisos de vue-toasted) ahora es global, en
   components/Notificaciones/NotificacionesToasts.vue: igual que las notificaciones. */
</style>

<style lang="scss" scoped>
@import "@/new-assets/styles";

.complete-data-button {
    display: flex;
    justify-content: center;
}

.not-included {
    font-size: 0.9rem;
    margin-top: 20px;
    margin-left: 15px;
}

.wizard-btn {
    background-color: $hf-sec-pine !important;
    color: #fff;
}

.wizard-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.icon-modal svg {
    width: 50px;
    height: 50px;
    margin-right: 20px;
    color: #f5d678;
}

.warn-title {
    color: #90721a;
    position: absolute;
    margin-top: 15px;
}

.icon-modal-suc svg {
    width: 50px;
    height: 50px;
    margin-right: 20px;
    color: #155724;
}

.highlight-square {
    background: #f7f7f7;
    padding: 10px;
    height: 100%;
}

.gateway-charge {
    margin-top: 20px;
    font-size: 0.9rem;
}

/* El mensaje trae saltos de línea propios en la traducción. */
.payment-message {
    white-space: pre-line;
    margin-bottom: 0;
}

.host-rules-note {
    margin-top: 16px;
    margin-bottom: 0;
    font-size: 0.85rem;
    font-style: italic;
    color: #6e6e76;
}

.payment-detail {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 3px 0;

    /* La etiqueta puede ocupar dos líneas si hace falta... */
    > div:first-child {
        min-width: 0;
    }

    /* ...pero el importe nunca se parte: queda entero, a la derecha. */
    > div:last-child {
        flex-shrink: 0;
        white-space: nowrap;
        text-align: right;
    }

    &--total {
        margin-top: 6px;
        padding-top: 8px;
        border-top: 1px solid #dcdcdc;
    }
}

.bottom {
    display: flex;
    justify-content: space-between;
}

.bottom-info {
    label {
        padding-left: 10px;
    }
}

.simulator-profile-image {
    display: flex;
    justify-content: flex-end;

    .circular-portrait {
        width: 150px;
        height: 150px;
        overflow: hidden;
        border-radius: 50%;
        border: 1px solid $hf-sec-pine;
    }

    .circular-portrait img {
        height: 100%;
        width: 100%;
    }
}

.circular-hint {
    text-align: center;
    border-radius: 50%;
    border: solid 1px rgb(196, 196, 196);
    width: 30px;
    height: 30px;
    padding: 2px;
    display: inline-block;
    margin-right: 10px;

    .fa-info {
        color: $hf-sec-pine;
    }

}

.reserve {
    width: 100%;
    font-size: 1.2em;
    font-weight: bold;
}

.icon-modal svg {
    width: 50px;
    height: 50px;
    margin-right: 20px;
    color: #f5d678;
}

#modal-pago {
    padding: 0;
}

.spinner {
    position: absolute;
    margin-right: 140px;
    width: 1.5rem;
    height: 1.5rem;
}

/* ── Resumen "Tu reserva" (modelo del 4%) ── */
.rs-card {
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 16px;
    padding: 20px;
}

.rs-today {
    background: linear-gradient(135deg, #fff1e6 0%, #fff8f2 100%);
    border-left: 3px solid $hf-orange;
    border-radius: 12px;
    padding: 16px 20px;
}

.rs-step-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $hf-orange;

    &--muted {
        color: #6e6e76;
    }
}

.rs-step-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #ffe6d4;
    color: $hf-orange;
    font-size: 0.75rem;

    &--active {
        background: $hf-orange;
        color: #fff;
    }
}

.rs-line {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 3px 0;

    > div:last-child {
        flex-shrink: 0;
        white-space: nowrap;
        text-align: right;
    }

    &--total {
        margin-top: 8px;
        padding-top: 10px;
        border-top: 1px solid #f5cfb3;
        font-size: 1.3rem;
        font-weight: 700;
        color: $hf-orange;
    }
}

.rs-note {
    margin-top: 8px;
    font-size: 0.8rem;
    color: #6e6e76;
}

.rs-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed #e4e4e4;
}

.rs-muted {
    color: #6e6e76;
    font-weight: normal;
    text-transform: none;
    letter-spacing: 0;
}

/* Ítems de pago al anfitrión: ícono, concepto + monto y la aclaración debajo. */
.rs-items {
    list-style: none;
    margin: 12px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.rs-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 14px;
    border: 1px solid #ececec;
    border-radius: 10px;
    background: #fafafa;

    &__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background: #fff1e6;
        color: $hf-orange;
        font-size: 0.9rem;
    }

    &__body {
        flex: 1;
        min-width: 0;
    }

    &__head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 12px;
        font-size: 0.95rem;
        color: #2a2a2a;
    }

    /* Mismo tamaño que el concepto y que "Arriendo restante": el monto no
       tiene que gritar más que el total de arriba. */
    &__amount {
        flex-shrink: 0;
        white-space: nowrap;
        font-weight: 600;
        color: #2a2a2a;
    }

    /* "al mes" en segundo plano, para que no alargue el monto. */
    &__unit {
        margin-left: 2px;
        font-size: 0.8rem;
        font-weight: 400;
        color: #6e6e76;
    }

    &__text {
        margin-top: 2px;
        font-size: 0.8rem;
        line-height: 1.4;
        color: #6e6e76;
    }
}

/* Calendario de pagos. En pantallas chicas se desplaza de costado en vez
   de aplastar las columnas. */
.rs-schedule-wrap {
    margin-top: 14px;
    border: 1px solid #ececec;
    border-radius: 10px;
    overflow-x: auto;
}

.rs-schedule {
    width: 100%;
    min-width: 440px;
    border-collapse: collapse;
    font-size: 0.85rem;

    th,
    td {
        padding: 10px 14px;
        text-align: left;
        vertical-align: middle;
        color: #2a2a2a;
    }

    th {
        background: #f3f3f3;
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: #6e6e76;
    }

    tbody tr + tr td {
        border-top: 1px solid #f0f0f0;
    }

    /* La fila de hoy se destaca con el mismo naranja del bloque 1. */
    &__row--today td {
        background: #fff8f2;
    }

    &__when {
        font-weight: 600;
        white-space: nowrap;
    }

    &__muted {
        color: #6e6e76;
    }

    &__amount {
        font-weight: 600;
        white-space: nowrap;
    }

    th:last-child,
    td:last-child {
        white-space: nowrap;
        text-align: right;
    }
}

.rs-badge {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 999px;
    background: #f0f0f0;
    color: #4a4a52;
    font-size: 0.75rem;
    font-weight: 600;

    &--hf {
        background: #ffe6d4;
        color: $hf-orange;
    }
}

.rs-grand-total {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
    padding: 12px 16px;
    border-radius: 10px;
    background: #2a2a2a;
    color: #fff;

    &__hint {
        margin-left: 4px;
        font-size: 0.85rem;
        color: #c9c9c9;
    }
}

.rs-warranty-title {
    margin-bottom: 10px;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $hf-orange;
}

.rs-warranty-boxes {
    margin-left: -5px;
    margin-right: -5px;

    > div {
        padding-left: 5px;
        padding-right: 5px;
        margin-bottom: 10px;
    }
}

.rs-box {
    height: 100%;
    padding: 8px 12px;
    border: 1px solid #f5dcc8;
    border-radius: 8px;
    background: #fffaf6;

    &__label {
        font-size: 0.8rem;
        color: #6e6e76;
    }

    &__value {
        font-weight: 700;
        color: #2a2a2a;
    }
}

.rs-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    background: #fff1e6;
    color: $hf-orange;
    font-size: 0.8rem;
}

</style>